/**
 * Resolves the hotel proxy number for Hotel details summary display.
 * Prefers operational phone lines (primary / active VoiceAndText), then stored
 * reservationProxyLine — never the hotel hotline when a real proxy exists.
 *
 * @param {{ reservationProxyLine?: string, phoneNumbers?: object[], reservationHotLine?: string }} params
 * @returns {string|null} Digits-only proxy, or null when none exists
 */
export function resolveHotelProxyNumber({
  reservationProxyLine,
  phoneNumbers,
  reservationHotLine
} = {}) {
  const digits = value => (value == null ? '' : String(value).replace(/\D/g, ''))
  const hotline = digits(reservationHotLine)
  const lines = Array.isArray(phoneNumbers) ? phoneNumbers : []

  const fromLine = line => {
    const number = digits(line?.phoneNumber ?? line?.PhoneNumber)
    return number || null
  }

  const isActive = line => {
    const status = (line?.status ?? line?.Status ?? '').toString()
    return status === 'Active'
  }

  const isPrimary = line => !!(line?.isPrimary ?? line?.IsPrimary)

  const capability = line => (line?.capability ?? line?.Capability ?? '').toString()

  const primary = lines.find(line => isPrimary(line) && isActive(line) && fromLine(line))
  if (primary) {
    return fromLine(primary)
  }

  const activeVoice = lines.find(
    line =>
      isActive(line) &&
      capability(line) === 'VoiceAndText' &&
      fromLine(line) &&
      fromLine(line) !== hotline
  )
  if (activeVoice) {
    return fromLine(activeVoice)
  }

  const stored = digits(reservationProxyLine)
  if (stored && stored !== hotline) {
    return stored
  }

  const otherActive = lines.find(
    line => isActive(line) && fromLine(line) && fromLine(line) !== hotline
  )
  if (otherActive) {
    return fromLine(otherActive)
  }

  return stored || null
}
